/**
 * The page data a home, list or hub route module's loader already has (web
 * plan W4.5; the entity pages' equivalent is @/hooks/entityPreload).
 *
 * The route module (src/routes/{home,brands,…}.tsx, @/components/seo/PageRoute)
 * provides the SEO server's answers, keyed by query (@/seo/routePreload), and
 * the page's hooks call `usePreloadedQuery` where they called `useQuery`:
 *
 * - an answer for exactly this query and these variables is shown at once,
 *   on the server and in the render that hydrates it, and the query is not
 *   sent (a guest's browser asks nothing the HTML already has);
 * - `refresh` (a signed-in viewer, `useViewerState`: the anonymous server
 *   copy has no `isFavorite` of theirs) sends it anyway; the server copy shows
 *   until their own answer lands, then theirs replaces it;
 * - any other query (the next page of a list, a section the server did not
 *   read) runs as before.
 *
 * `<PageHead/>` renders nothing while a route preload is present: the route's
 * `meta` owns the head. Null everywhere else (the SPA routes, an in-app
 * navigation, where `clientLoader` has no server answer).
 */
import { createContext, useContext } from 'react';
import {
  useQuery,
  type DocumentNode,
  type OperationVariables,
  type QueryHookOptions,
  type QueryResult,
  type TypedDocumentNode,
} from '@apollo/client';
import { preloadedAnswer, type RoutePreloadData } from '@/seo/routePreload';

export interface RoutePreload {
  /** The server's answers by `preloadKey`. */
  queries: RoutePreloadData;
  /** Also send the queries (a signed-in viewer: their own copy replaces the server's). */
  refresh: boolean;
  /** Operations with no viewer-specific fields: never re-sent for `refresh` (the server copy is complete). */
  anonymousOnly?: readonly string[];
}

export const RoutePreloadContext = createContext<RoutePreload | null>(null);

export function useRoutePreload(): RoutePreload | null {
  return useContext(RoutePreloadContext);
}

/** The document's one named operation (`query PublishedBrandsRich …` → `PublishedBrandsRich`). */
export function operationNameOf(query: DocumentNode): string | null {
  for (const definition of query.definitions) {
    if (definition.kind === 'OperationDefinition') return definition.name?.value ?? null;
  }
  return null;
}

/** `useQuery`, answered from the route preload when it has this exact query (see the file comment). */
export function usePreloadedQuery<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: QueryHookOptions<TData, TVariables> = {},
): QueryResult<TData, TVariables> {
  const preload = useRoutePreload();
  const served = options.skip
    ? undefined
    : (preloadedAnswer(preload?.queries, operationNameOf(query), options.variables) as TData | undefined);
  const held = served !== undefined;
  const name = operationNameOf(query);
  const refresh = Boolean(preload?.refresh) && !(name !== null && preload?.anonymousOnly?.includes(name));
  const result = useQuery<TData, TVariables>(query, {
    ...options,
    skip: Boolean(options.skip) || (held && !refresh),
  });
  if (!held || result.data !== undefined) return result;
  // The server's answer for this URL: no loading state, no error, until the
  // viewer's own copy (refresh) replaces it.
  return { ...result, data: served, loading: false, error: undefined };
}
